/**
 * Run Q / Q-4 — the clinic's module flags, fetched once per session.
 *
 * Same caching shape as useClinicBranding: one request, shared by every
 * consumer, so the sidebar and the route guard cannot disagree.
 */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { readClinicModules, MODULES_DEFAULT, type ClinicModules } from '@/lib/clinicModules';

let cached: ClinicModules | null = null;
let inFlight: Promise<void> | null = null;
const listeners = new Set<(m: ClinicModules) => void>();

/**
 * Drop the cache and refetch, telling every mounted consumer.
 *
 * The Settings toggle needs this: the sidebar reads the flag once per session,
 * so without a notification, switching the Front Desk module on left the nav
 * unchanged until a full page reload — the toggle looked broken. Same shape as
 * clearBrandingCache(), which the settings page already calls after a save.
 */
export function clearClinicModulesCache(): void {
  cached = null;
  inFlight = null;
  if (listeners.size === 0) return;
  void (async () => {
    try {
      const res = await api.get<Record<string, unknown>>('/clinics/settings');
      cached = readClinicModules(res.data);
    } catch {
      cached = MODULES_DEFAULT;
    }
    for (const l of listeners) l(cached ?? MODULES_DEFAULT);
  })();
}

export interface ClinicModulesState extends ClinicModules { isLoading: boolean }

export function useClinicModules(): ClinicModulesState {
  const [modules, setModules] = useState<ClinicModules | null>(cached);
  const [isLoading, setIsLoading] = useState(!cached);

  useEffect(() => {
    let alive = true;
    const onChange = (m: ClinicModules): void => { if (alive) { setModules(m); setIsLoading(false); } };
    listeners.add(onChange);
    if (cached) { setModules(cached); setIsLoading(false); return () => { alive = false; listeners.delete(onChange); }; }
    if (!inFlight) {
      inFlight = (async () => {
        try {
          const res = await api.get<Record<string, unknown>>('/clinics/settings');
          cached = readClinicModules(res.data);
        } catch {
          // A failed settings read must not flash a module on and off — hold
          // the safe default until a later mount succeeds.
          cached = MODULES_DEFAULT;
          inFlight = null;
        }
      })();
    }
    void inFlight.then(() => {
      if (!alive) return;
      setModules(cached ?? MODULES_DEFAULT);
      setIsLoading(false);
    });
    return () => { alive = false; listeners.delete(onChange); };
  }, []);

  return { ...(modules ?? MODULES_DEFAULT), isLoading };
}
